import { useCallback } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import toPairs from 'lodash/toPairs'

export const RETURN_URL_QUERY = 'returnUrl'

export type RelativeUrlParts = {
  pathOnly: string
  search: string
  hash: string
  hashValue: string
}

export function splitRelativeUrl(relative: string): RelativeUrlParts {
  const hashIndex = relative.indexOf('#')
  const beforeHash = hashIndex >= 0 ? relative.slice(0, hashIndex) : relative
  const hashValue = hashIndex >= 0 ? relative.slice(hashIndex + 1) : ''
  const qIndex = beforeHash.indexOf('?')
  const pathOnly = qIndex >= 0 ? beforeHash.slice(0, qIndex) : beforeHash
  const search = qIndex >= 0 ? beforeHash.slice(qIndex) : ''
  return {
    pathOnly,
    search,
    hash: hashValue ? `#${hashValue}` : '',
    hashValue
  }
}

export function parseReturnUrl(raw: string | null | undefined): string | null {
  if (raw == null) return null
  const trimmed = raw.trim()
  if (trimmed.length === 0) return null
  if (!trimmed.startsWith('/')) return null
  if (trimmed.startsWith('//')) return null
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)) return null
  return trimmed
}

export function returnUrlFromSearchParams(searchParams: URLSearchParams): string | null {
  return parseReturnUrl(searchParams.get(RETURN_URL_QUERY))
}

export function withReturnUrl(destinationPath: string, returnTo: string): string {
  const safeReturn = parseReturnUrl(returnTo.startsWith('/') ? returnTo : `/${returnTo}`)
  if (safeReturn == null) return destinationPath

  const { pathOnly, search, hash } = splitRelativeUrl(destinationPath)
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  params.set(RETURN_URL_QUERY, safeReturn)
  const qs = params.toString()
  return `${pathOnly}${qs ? `?${qs}` : ''}${hash}`
}

export function locationToReturnPath(loc: { pathname: string; search?: string; hash?: string }): string {
  return `${loc.pathname}${loc.search ?? ''}${loc.hash ?? ''}`
}

export function returnUrlHref(
  destinationPath: string,
  loc: { pathname: string; search?: string; hash?: string }
): string {
  return withReturnUrl(destinationPath, locationToReturnPath(loc))
}

export function enhanceReturnUrl(returnUrl: string, opts?: { query?: Record<string, string>; hash?: string }): string {
  const safe = parseReturnUrl(returnUrl)
  if (safe == null) return returnUrl

  const { pathOnly, search, hashValue: existingHash } = splitRelativeUrl(safe)
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  if (opts?.query) {
    for (const [key, value] of toPairs(opts.query)) {
      params.set(key, value)
    }
  }
  const qs = params.toString()
  const hash = opts?.hash != null ? opts.hash : existingHash
  return `${pathOnly}${qs ? `?${qs}` : ''}${hash ? `#${hash}` : ''}`
}

export function returnUrlHasPathSegment(returnUrl: string, segment: string): boolean {
  const safe = parseReturnUrl(returnUrl)
  if (safe == null) return false
  const { pathOnly } = splitRelativeUrl(safe)
  return pathOnly.split('/').filter(Boolean).includes(segment)
}

export type MapReturnUrl = (returnUrl: string) => string

export function useReturnUrlExit(defaultPath: string): {
  exit: () => void
  exitAfterSave: (mapReturn?: MapReturnUrl) => void
} {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const exit = useCallback(() => {
    const returnUrl = returnUrlFromSearchParams(searchParams)
    navigate(returnUrl ?? defaultPath)
  }, [navigate, searchParams, defaultPath])

  const exitAfterSave = useCallback(
    (mapReturn?: MapReturnUrl) => {
      const returnUrl = returnUrlFromSearchParams(searchParams)
      if (returnUrl == null) {
        navigate(defaultPath)
        return
      }
      navigate(mapReturn != null ? mapReturn(returnUrl) : returnUrl)
    },
    [navigate, searchParams, defaultPath]
  )

  return { exit, exitAfterSave }
}

export function useNavigateWithReturnUrl(): {
  href: (destinationPath: string) => string
  navigateWithReturnUrl: (destinationPath: string) => void
} {
  const navigate = useNavigate()
  const location = useLocation()

  const href = useCallback((destinationPath: string) => returnUrlHref(destinationPath, location), [location])

  const navigateWithReturnUrl = useCallback(
    (destinationPath: string) => {
      navigate(href(destinationPath))
    },
    [navigate, href]
  )

  return { href, navigateWithReturnUrl }
}
